import axios, { AxiosInstance, AxiosRequestConfig, InternalAxiosRequestConfig } from "axios"

/** Live API – used when `npm run build` (NODE_ENV=production). */
const LIVE_API = "https://clayhostingapi.soldx.in"
/** Local backend – used when `npm start`. */
const LOCAL_API = "http://localhost:8001"

export const API_BASE =
  (process.env.REACT_APP_API_BASE_URL || "").trim() ||
  (process.env.NODE_ENV === "production" ? LIVE_API : LOCAL_API)

const isSessionInvalidMessage = (msg?: string) =>
  msg && /login again|another device|session expired|token expired|unauthorized|invalid token/i.test(String(msg))

const triggerLogout = () => {
  localStorage.removeItem("authUser")
  window.location.href = "/login"
}

const getAuthToken = (): string | null => {
  try {
    const authUser = localStorage.getItem("authUser")
    if (authUser) {
      const obj = JSON.parse(authUser)
      return obj.accessToken || obj.token || null
    }
  } catch {
    // ignore
  }
  return null
}

const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE,
})

apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getAuthToken()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

apiClient.interceptors.response.use(
  (response) => {
    const data = response.data
    if (data && typeof data === "object" && data.success === false) {
      const msg = data.message || data.error || ""
      if (isSessionInvalidMessage(msg)) {
        triggerLogout()
        return Promise.reject(new Error(msg || "Session expired"))
      }
    }
    return response
  },
  (error) => {
    if (error?.response?.status === 401) {
      const hadToken = error?.config?.headers?.Authorization
      if (hadToken) {
        triggerLogout()
      }
    } else if (error?.response?.data) {
      const msg = error.response.data.message || error.response.data.error || ""
      if (isSessionInvalidMessage(msg)) {
        triggerLogout()
      }
    }
    return Promise.reject(error)
  }
)

export default apiClient

/** GET request - token auto-attached */
export const apiGet = <T = any>(url: string, config?: AxiosRequestConfig) =>
  apiClient.get<T>(url, config).then((r) => r.data)

/** POST request - token auto-attached */
export const apiPost = <T = any>(url: string, data?: any, config?: AxiosRequestConfig) =>
  apiClient.post<T>(url, data, config).then((r) => r.data)

/** PUT request - token auto-attached */
export const apiPut = <T = any>(url: string, data?: any, config?: AxiosRequestConfig) =>
  apiClient.put<T>(url, data, config).then((r) => r.data)

/** PATCH request - token auto-attached */
export const apiPatch = <T = any>(url: string, data?: any, config?: AxiosRequestConfig) =>
  apiClient.patch<T>(url, data, config).then((r) => r.data)

/** DELETE request - token auto-attached */
export const apiDelete = <T = any>(url: string, config?: AxiosRequestConfig) =>
  apiClient.delete<T>(url, config).then((r) => r.data)
